/**
 * CSV Export Utility
 *
 * Utility functions for exporting data to CSV format
 */
import { TYPE_DISPLAY_NAMES } from '../config/requestTypeConstants';

/**
 * Converts data to CSV format and triggers download
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions with id and label
 * @param {string} filename - Name of the file to download
 */
export const exportToCSV = (data, columns, filename = 'export.csv') => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get visible columns only
  const visibleColumns = columns.filter(col => col.visible);

  // Create CSV header row
  const headers = visibleColumns.map(col => col.label);

  // Create CSV data rows
  const rows = data.map(item => {
    return visibleColumns.map(col => {
      const value = getCellValue(item, col.id);

      // Escape and format value for CSV
      if (value === null || value === undefined || value === '') {
        return '';
      }

      // Convert to string and escape quotes
      const stringValue = String(value).replace(/"/g, '""');

      // Wrap in quotes if contains comma, newline, or quote
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue}"`;
      }

      return stringValue;
    });
  });

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  // Create blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Extracts cell value based on column id
 * @param {Object} item - Data item
 * @param {string} columnId - Column identifier
 * @returns {string|number} - Cell value
 */
const getCellValue = (item, columnId) => {
  switch (columnId) {
    case 'request_number':
      return item.request_number || '';

    case 'product':
      return item.product?.product_name || '';

    case 'ski_release':
      return item.skiRelease?.full_name || '';

    case 'request_type':
      const typeCode = item.requestType?.type_code || '';
      if (item.gpr_subtype) {
        return `GPR - ${item.gpr_subtype}`;
      } else if (typeCode === 'GPR' && item.answers) {
        const gprSubtypeAnswer = item.answers.find(a => a.question_key === 'gpr_subtype');
        return gprSubtypeAnswer?.answer_value
          ? `GPR - ${gprSubtypeAnswer.answer_value}`
          : (TYPE_DISPLAY_NAMES[typeCode] || typeCode);
      }
      return TYPE_DISPLAY_NAMES[typeCode] || typeCode;

    case 'status':
      return item.status || '';

    case 'owner':
      return item.owner?.fullName || '';

    case 'created_by':
      return item.creator ? `${item.creator.first_name} ${item.creator.last_name}` : '';

    case 'created_at':
      return item.created_at ? new Date(item.created_at).toLocaleDateString() : '';

    case 'updated_at':
      return item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '';

    case 'location':
      return item.location || '';

    case 'work_areas':
      return item.work_areas || '';

    case 'categorization':
      return item.product?.categorization || '';

    default:
      return '';
  }
};
